import { Link } from "react-router-dom";
import {ShieldCheck, Zap, BarChart3, Lock, ArrowRight, Sparkles, CreditCard, Banknote, ArrowRightLeft, Plane, Clock, Gift, Percent, DollarSign, } from 'lucide-react';
import UniversalCalculator from "@/components/UniversalCalculator";
import FAQ from "@/components/FAQ";
import Disclaimer from "@/components/Disclaimer";
import { ALL_CALCULATORS, SITE_NAME, SITE_URL } from "@/lib/site";
import { useSeo, faqSchema } from "@/lib/seo";

const HOME_FAQS = [
  {
    q: "How is credit card interest calculated?",
    a: "Most US issuers use the average daily balance method. They divide your APR by 365 to get a daily periodic rate, multiply that by your average daily balance, and then by the number of days in your billing cycle. Our homepage calculator applies this exact formula.",
  },
  {
    q: "What is a cash advance fee?",
    a: "A cash advance fee is charged when you borrow cash against your credit card — at an ATM, with a convenience check, or on cash-like transactions. It is usually the greater of a flat minimum (e.g. $10) or a percentage of the amount (typically 3–5%). Interest starts immediately with no grace period.",
  },
  {
    q: "How do balance transfer fees work?",
    a: "When you move debt from one card to another, the new issuer normally charges a one-time fee of 3% to 5% of the transferred amount. If the card has a 0% intro APR, the transfer can still save money — our balance transfer calculator compares both scenarios for you.",
  },
  {
    q: "What is a foreign transaction fee?",
    a: "A foreign transaction fee is a surcharge some card issuers add to purchases processed outside your home country or in a foreign currency. It is typically 1–3% of the converted amount. Travel-optimized cards often charge 0%.",
  },
  {
    q: "Is an annual fee worth paying?",
    a: "An annual fee is worth it when your rewards, statement credits, and perks exceed the fee based on how you actually spend. Use the annual fee calculator to estimate net value and the break-even spending level for any card you're considering.",
  },
  {
    q: "How can I avoid credit card fees?",
    a: "Pay your statement balance in full and on time, avoid cash advances, pick no-foreign-fee cards for travel, and evaluate annual fees against real rewards. Each of our calculators shows the dollar impact of each fee so you can make informed choices.",
  },
];

function Home() {
  useSeo({
    title: "Credit Card Fee Calculator — Estimate Interest, Cash Advance & Foreign Fees",
    description: "Free credit card fee calculator. Estimate interest, cash advance fees, balance transfer costs, foreign transaction fees, annual fee value, late fees and surcharges in seconds.",
    path: "/",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_URL}/?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
        logo: "https://lumi.new/lumi.ing/logo.png",
      },
      {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "Credit Card Fee Calculator",
        applicationCategory: "FinanceApplication",
        operatingSystem: "Any",
        url: SITE_URL,
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      },
      faqSchema(HOME_FAQS),
    ],
  });

  return (
    <>
      {/* Hero */}
      <section className="relative pt-10 md:pt-16 pb-10 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50/60 via-white to-white -z-10" />
        <div className="absolute top-[-120px] right-[-120px] w-[380px] h-[380px] bg-emerald-100 rounded-full blur-3xl opacity-50 -z-10" />
        <div className="absolute top-[-60px] left-[-80px] w-[320px] h-[320px] bg-blue-100 rounded-full blur-3xl opacity-60 -z-10" />

        <div className="container-tight">
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-2 lg:pt-6 reveal">
              <span className="pill mb-5"><Sparkles size={12} /> Trusted estimator · Free · No signup</span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05] mb-5">
                Credit card fees, <span className="text-[#0A5FFF]">decoded in seconds.</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-6 max-w-xl">
                Estimate interest charges, cash advance costs, balance transfer fees, foreign transaction fees, late fees and annual fee value — all in one clear calculator. Built for cardholders, not marketers.
              </p>
              <div className="flex flex-wrap items-center gap-3 mb-8">
                <a href="#calc" className="btn-primary inline-flex items-center gap-2">
                  Use the calculator <ArrowRight size={16} />
                </a>
                <Link to="/faq" className="btn-ghost">How it works</Link>
              </div>
              <div className="grid grid-cols-3 gap-4 max-w-md">
                <Stat v="10" l="calculators" />
                <Stat v="$0" l="to use" />
                <Stat v="100%" l="private" />
              </div>
            </div>

            <div id="calc" className="lg:col-span-3 reveal">
              <UniversalCalculator />
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-slate-200 bg-white">
        <div className="container-tight py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <TrustItem icon={ShieldCheck} title="No data stored" desc="All math runs in your browser." />
          <TrustItem icon={Zap} title="Instant results" desc="Updates as you type." />
          <TrustItem icon={BarChart3} title="Clear formulas" desc="Every page shows the math." />
          <TrustItem icon={Lock} title="No signup" desc="No email required to calculate." />
        </div>
      </section>

      {/* Popular calculators */}
      <section className="py-16 md:py-20">
        <div className="container-tight">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <span className="pill mb-3">Explore</span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900">Popular credit card fee calculators</h2>
              <p className="text-slate-600 mt-2 max-w-2xl">Each tool targets one specific fee so you can dig into the numbers that matter for your card and spending pattern.</p>
            </div>
            <Link to="/faq" className="text-sm font-semibold text-[#0A5FFF] inline-flex items-center gap-1">See the FAQ hub <ArrowRight size={14} /></Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ALL_CALCULATORS.slice(1).map((c, i) => {
              const icons = [Percent, Banknote, ArrowRightLeft, Plane, DollarSign, CreditCard, Clock, Gift, Percent];
              const Icon = icons[i % icons.length];
              return (
                <Link key={c.to} to={c.to} className="group card-soft p-5 hover:border-[#0A5FFF] transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 text-[#0A5FFF] flex items-center justify-center">
                      <Icon size={18} />
                    </div>
                    <span className="pill">{c.tag}</span>
                  </div>
                  <h3 className="font-serif text-lg font-semibold text-slate-900 group-hover:text-[#0A5FFF] transition-colors mb-1">
                    {c.title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">{c.desc}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-[#0A5FFF]">
                    Open <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 bg-slate-50 border-y border-slate-200">
        <div className="container-tight">
          <span className="pill mb-3">How it works</span>
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-10 max-w-2xl">Three steps to a clearer picture of credit card costs</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { n: "01", t: "Pick the fee type", d: "Choose the calculator that matches the charge you're trying to understand — interest, cash advance, foreign transaction, late fee and more." },
              { n: "02", t: "Enter your numbers", d: "Plug in your balance, APR, or purchase amount. The built-in defaults reflect common issuer terms in the United States." },
              { n: "03", t: "See the breakdown", d: "Results appear instantly with the total cost, individual components, and a plain-English explanation of the formula." },
            ].map((s) => (
              <div key={s.n} className="card-soft p-6">
                <div className="font-serif text-3xl text-[#0A5FFF] mb-3">{s.n}</div>
                <h3 className="font-serif text-xl font-semibold text-slate-900 mb-2">{s.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types of fees */}
      <section className="py-16 md:py-20">
        <div className="container-tight">
          <span className="pill mb-3">Reference</span>
          <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4 max-w-3xl">The main types of credit card fees</h2>
          <p className="text-slate-600 max-w-2xl mb-10">Credit cards can carry many charges. Knowing what each fee is — and when it applies — helps you avoid surprise costs and pick the right card for your life.</p>

          <div className="grid md:grid-cols-2 gap-5">
            {[
              { t: "Interest / finance charge", d: "Charged on balances you don't pay in full by the due date. Based on your APR and average daily balance.", to: "/finance-charge-calculator" },
              { t: "Cash advance fee", d: "A combined up-front fee and daily interest when you take cash against your credit line.", to: "/cash-advance-calculator" },
              { t: "Balance transfer fee", d: "A one-time percentage fee, usually 3–5%, charged when moving debt from another card.", to: "/balance-transfer-fee-calculator" },
              { t: "Foreign transaction fee", d: "A small percentage added to purchases abroad or in foreign currencies — often 1–3%.", to: "/foreign-transaction-fee-calculator" },
              { t: "Late payment fee", d: "A flat penalty if you miss your due date, often stacked on top of added interest.", to: "/late-fee-calculator" },
              { t: "Annual fee", d: "A yearly charge to hold the card. Premium cards may offset the fee with rewards and credits.", to: "/annual-fee-calculator" },
            ].map((f) => (
              <Link key={f.t} to={f.to} className="group card-soft p-5 flex items-start gap-4 hover:border-[#0A5FFF] transition-colors">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                  <CreditCard size={18} />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-semibold text-slate-900 group-hover:text-[#0A5FFF] transition-colors">{f.t}</h3>
                  <p className="text-sm text-slate-600 mt-1">{f.d}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why use */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 to-[#0B1220] text-white">
        <div className="container-tight">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs font-semibold mb-4 text-blue-200">
            Why use this calculator
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4 max-w-3xl">Decide with numbers, not guesses.</h2>
          <p className="text-slate-300 max-w-2xl mb-10 leading-relaxed">
            Credit card issuers are legally required to disclose fees, but the math is rarely easy to read. Our calculators do the arithmetic in plain English so you can compare cards, plan payments and avoid surprise charges.
          </p>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { t: "Save money", d: "Spot costly fees before they hit your statement." },
              { t: "Plan payments", d: "See how interest changes with different payment amounts." },
              { t: "Travel smarter", d: "Know exactly what a trip abroad will cost on your card." },
              { t: "Compare cards", d: "Evaluate annual fees against real rewards value." },
            ].map((b) => (
              <div key={b.t} className="rounded-xl bg-white/5 border border-white/10 p-5">
                <h3 className="font-serif text-lg font-semibold mb-1">{b.t}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ items={HOME_FAQS} />

      {/* Disclaimer */}
      <section className="pb-16">
        <div className="container-tight">
          <Disclaimer />
        </div>
      </section>
    </>
  );
}

function Stat({ v, l }: { v: string; l: string }) {
  return (
    <div>
      <div className="font-serif text-2xl md:text-3xl font-semibold text-slate-900">{v}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">{l}</div>
    </div>
  );
}

function TrustItem({ icon: Icon, title, desc }: { icon: any; title: string; desc: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-blue-50 text-[#0A5FFF] flex items-center justify-center shrink-0">
        <Icon size={16} />
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500">{desc}</div>
      </div>
    </div>
  );
}

export default Home;
